// Detalle de UN Reporte Operativo — abierto al hacer click en una
// notificación de "Nuevo reporte operativo" (ver informe "Sprint
// Operativo", Parte 4). 👍Aprobar / 👎Rechazar / ✏️Editar / Cerrar.
// "Editar" reutiliza el mismo OverrideEditModal (mode="approve") — nunca
// una segunda UI de Turbo/Doble/Manual.
import React, { useEffect, useState } from "react";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Input from "../ui/Input";
import { authFetch } from "../../lib/authFetch";
import OverrideEditModal, { DeliveryDetail, REPORT_TYPE_LABELS, ReportType, money } from "./OverrideEditModal";

type ReportDetail = {
  id: number;
  lightdata_id: number | string;
  tracking: string | null;
  store_id: number | null;
  store_name: string | null;
  driver_id: number | null;
  driver_name: string | null;
  manual_price: number | null;
  force_turbo: boolean;
  double_price: boolean;
  report_type: ReportType;
  observation: string | null;
  status: "PENDIENTE" | "APROBADO" | "RECHAZADO";
  requested_by_name: string | null;
  requested_at: string;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
};

type Props = {
  open: boolean;
  reportId: number | string | null;
  token: string | null;
  onClose: () => void;
  // Se llama tras aprobar/rechazar/editar-y-aprobar, para que la pantalla
  // que dispara esto (ej. la lista de reportes) refresque si corresponde.
  onResolved?: () => void;
};

const overlayStyle: React.CSSProperties = { position: "fixed", inset: 0, zIndex: 130, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" };
const backdropStyle: React.CSSProperties = { position: "absolute", inset: 0, background: "rgba(0,0,0,0.65)" };
const stateStyle: React.CSSProperties = { padding: "24px 0", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };

const STATUS_LABEL: Record<ReportDetail["status"], string> = {
  PENDIENTE: "Pendiente",
  APROBADO: "Aprobado",
  RECHAZADO: "Rechazado",
};

export default function ReportDetailModal({ open, reportId, token, onClose, onResolved }: Props) {
  const [report, setReport] = useState<ReportDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [editing, setEditing] = useState(false);

  const load = React.useCallback(() => {
    if (!reportId || !token) return;
    setLoading(true);
    setError(null);
    return authFetch(`/v1/reports/${reportId}`, { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se pudo cargar el reporte");
        setReport(payload.report);
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setLoading(false));
  }, [reportId, token]);

  useEffect(() => {
    if (!open) return;
    setReport(null);
    setRejecting(false);
    setRejectReason("");
    setEditing(false);
    load();
  }, [open, load]);

  if (!open) return null;

  const approve = async () => {
    if (!reportId) return;
    setActing(true);
    try {
      const res = await authFetch(`/v1/reports/${reportId}/approve`, { method: "PUT", token, json: true, body: "{}" });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se pudo aprobar");
      onResolved?.();
      onClose();
    } catch (e: any) {
      setError(e.message || "No se pudo aprobar el reporte");
    } finally {
      setActing(false);
    }
  };

  const reject = async () => {
    if (!reportId) return;
    setActing(true);
    try {
      const res = await authFetch(`/v1/reports/${reportId}/reject`, {
        method: "PUT", token, json: true,
        body: JSON.stringify({ rejection_reason: rejectReason.trim() || null }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "No se pudo rechazar");
      onResolved?.();
      onClose();
    } catch (e: any) {
      setError(e.message || "No se pudo rechazar el reporte");
    } finally {
      setActing(false);
    }
  };

  // Delivery sintético para OverrideEditModal(mode="approve") — mismo
  // patrón que ReportesOperativos.tsx/EnvioLegacyDetailModal.tsx: no hay
  // pricing en vivo acá, solo lo que el Coordinador pidió.
  const editDelivery: DeliveryDetail | null = report
    ? {
        lightdata_id: report.lightdata_id,
        tracking: report.tracking,
        fecha_colecta: null,
        destinatario: null,
        direccion: null,
        original_cp: null,
        cp: null,
        has_postal_code_override: false,
        tier: null,
        tariff_name: null,
        estado: null,
        is_turbo: report.force_turbo,
        turbo_source: report.force_turbo ? "MANUAL" : null,
        turbo_price: null,
        is_double: report.double_price,
        calculated_price: null,
        manual_price: report.manual_price,
        final_price: null,
        pricing_status: "PENDING",
        pricing_reason: null,
        has_override: false,
        override_observation: report.observation,
      }
    : null;

  return (
    <div style={overlayStyle}>
      <div style={backdropStyle} onClick={() => !acting && onClose()} />
      <div style={{ position: "relative", width: "100%", maxWidth: "480px" }}>
        <Card padding="20px" style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: "var(--text-primary)" }}>Nuevo reporte operativo</h3>
          </div>

          {loading && <div style={stateStyle}>Cargando…</div>}
          {!loading && error && (
            <>
              <div style={{ ...stateStyle, color: "var(--danger, #d1242f)" }}>{error}</div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="secondary" onClick={onClose}>Cerrar</Button>
              </div>
            </>
          )}

          {!loading && report && (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: "13px" }}>
                {report.store_name && (
                  <div><span style={{ color: "var(--text-secondary)" }}>Store: </span>{report.store_name}</div>
                )}
                {report.driver_name && (
                  <div><span style={{ color: "var(--text-secondary)" }}>Chofer: </span>{report.driver_name}</div>
                )}
                <div><span style={{ color: "var(--text-secondary)" }}>Tracking: </span>{report.tracking || report.lightdata_id}</div>
                <div><span style={{ color: "var(--text-secondary)" }}>Motivo: </span>{REPORT_TYPE_LABELS[report.report_type]}</div>
                {report.manual_price !== null && (
                  <div><span style={{ color: "var(--text-secondary)" }}>Precio propuesto: </span>{money(report.manual_price)}</div>
                )}
                {(report.force_turbo || report.double_price) && (
                  <div>
                    {report.force_turbo && <span style={{ marginRight: "8px" }}>⚡ Turbo</span>}
                    {report.double_price && <span>×2 Doble</span>}
                  </div>
                )}
                <div><span style={{ color: "var(--text-secondary)" }}>Observación: </span>{report.observation || "—"}</div>
                <div><span style={{ color: "var(--text-secondary)" }}>Solicitado por: </span>{report.requested_by_name || "—"}</div>
                <div><span style={{ color: "var(--text-secondary)" }}>Estado: </span>{STATUS_LABEL[report.status]}</div>
                {report.status === "RECHAZADO" && report.rejection_reason && (
                  <div><span style={{ color: "var(--text-secondary)" }}>Motivo de rechazo: </span>{report.rejection_reason}</div>
                )}
              </div>

              {rejecting && (
                <Input
                  label="Motivo del rechazo (opcional)"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  autoFocus
                />
              )}

              {report.status === "PENDIENTE" ? (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {rejecting ? (
                    <>
                      <Button variant="secondary" onClick={() => setRejecting(false)} disabled={acting}>Cancelar</Button>
                      <Button variant="danger" onClick={reject} disabled={acting}>{acting ? "…" : "👎 Confirmar rechazo"}</Button>
                    </>
                  ) : (
                    <>
                      <Button variant="secondary" onClick={onClose} disabled={acting}>Cerrar</Button>
                      <Button variant="secondary" onClick={() => setEditing(true)} disabled={acting}>✏️ Editar</Button>
                      <Button variant="danger" onClick={() => setRejecting(true)} disabled={acting}>👎 Rechazar</Button>
                      <Button onClick={approve} disabled={acting}>{acting ? "…" : "👍 Aprobar"}</Button>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button variant="secondary" onClick={onClose}>Cerrar</Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      <OverrideEditModal
        open={editing}
        mode="approve"
        entityLabel={report?.store_name ? `Store: ${report.store_name}` : report?.driver_name ? `Chofer: ${report.driver_name}` : null}
        token={token}
        delivery={editDelivery}
        approveUrl={`/v1/reports/${reportId}/approve`}
        onClose={() => setEditing(false)}
        onSaved={() => {
          setEditing(false);
          onResolved?.();
          onClose();
        }}
      />
    </div>
  );
}
